import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  FormControlLabel,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { executeUploadedSlideIngest } from 'api/uploads';
import { DialogChromeFooter, DialogChromeHeader, dialogPrimaryActionSx } from 'components/atoms/Dialog/DialogChrome';
import { ActionStepper } from 'components/StudyDashboard/ProceduresPage/Actions/ActionStepper';
import { JobNameAndDescriptionStep } from 'components/StudyDashboard/ProceduresPage/Actions/utils';
import { UploadDashboardRow } from 'interfaces/fileList';
import { filter, includes, map, size, some } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { getUploadDashboardLifecycle, isUploadIngestActionAvailable } from './helpers';
import UploadedFilesTreeSelector from './UploadedFilesTreeSelector';

interface BulkUploadIngestExecutionDialogProps {
  rows: UploadDashboardRow[];
  initialSelectedUploadKeys?: string[];
  studyId: string;
  labId: string;
  canReingestUploadedSlides: boolean;
  onClose: () => void;
}

interface BulkUploadIngestExecutionFormValues {
  selectedUploadKeys: string[];
  jobName: string;
  jobDescription: string;
  confirmReingest: boolean;
}

const SELECT_FOLDER_AND_FILES_STEP_INDEX = 0;
const JOB_METADATA_STEP_INDEX = 1;
const CONFIRM_REINGEST_STEP_INDEX = 3;

const getUploadSelectionError = (keys: string[], rows: UploadDashboardRow[], canReingest: boolean) => {
  if (!keys.length) return 'Select at least one file to queue for ingest.';
  const eligibleKeys = new Set(
    rows.flatMap((row) =>
      row.uploadKey && isUploadIngestActionAvailable(row, getUploadDashboardLifecycle(row), canReingest)
        ? [row.uploadKey]
        : []
    )
  );
  if (keys.some((key) => !eligibleKeys.has(key))) {
    return 'Some selected uploads are no longer eligible for ingest. Review the selection.';
  }
  if (new Set(keys.map((key) => key.split('/').slice(0, -1).join('/'))).size > 1) {
    return 'Select files from one folder for each ingest run.';
  }
  return null;
};

const getDefaultSelectedUploadKeys = (uploads: UploadDashboardRow[]) =>
  uploads.reduce<string[]>((selectedUploadKeys, uploadRow) => {
    if (
      uploadRow.uploadCategory === 'awaitingIngest' &&
      uploadRow.uploadKey &&
      isUploadIngestActionAvailable(uploadRow, getUploadDashboardLifecycle(uploadRow), false)
    ) {
      selectedUploadKeys.push(uploadRow.uploadKey);
    }

    return selectedUploadKeys;
  }, []);

const BulkUploadIngestExecutionDialog = ({
  rows,
  initialSelectedUploadKeys,
  studyId,
  labId,
  canReingestUploadedSlides,
  onClose,
}: BulkUploadIngestExecutionDialogProps) => {
  const queryClient = useQueryClient();
  const defaultSelectedUploadKeys = React.useMemo(
    () =>
      initialSelectedUploadKeys
        ? rows
            .filter(
              (row) =>
                row.uploadKey &&
                initialSelectedUploadKeys.includes(row.uploadKey) &&
                isUploadIngestActionAvailable(row, getUploadDashboardLifecycle(row), canReingestUploadedSlides)
            )
            .map((row) => row.uploadKey!)
        : getDefaultSelectedUploadKeys(rows),
    [rows, initialSelectedUploadKeys, canReingestUploadedSlides]
  );
  const rowsResetKey = React.useMemo(
    () =>
      `${JSON.stringify(initialSelectedUploadKeys ?? null)}:${map(
        rows,
        (uploadRow) => `${uploadRow.id}:${uploadRow.uploadCategory}:${uploadRow.uploadKey || ''}`
      ).join('|')}`,
    [rows, initialSelectedUploadKeys]
  );
  const previousRowsResetKeyRef = React.useRef<string | null>(null);
  const [activeStep, setActiveStep] = React.useState(0);
  const [isStepFailed, setIsStepFailed] = React.useState<Record<number, boolean>>({});
  const formMethods = useForm<BulkUploadIngestExecutionFormValues>({
    mode: 'onChange',
    defaultValues: {
      selectedUploadKeys: defaultSelectedUploadKeys,
      jobName: '',
      jobDescription: '',
      confirmReingest: false,
    },
  });
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    trigger,
    watch,
  } = formMethods;
  const selectedUploadKeys = watch('selectedUploadKeys');
  const selectionError = getUploadSelectionError(selectedUploadKeys, rows, canReingestUploadedSlides);
  const jobName = watch('jobName');
  const confirmReingest = watch('confirmReingest');
  const selectedUploads = React.useMemo(
    () =>
      filter(rows, (uploadRow) => Boolean(uploadRow.uploadKey) && includes(selectedUploadKeys, uploadRow.uploadKey)),
    [rows, selectedUploadKeys]
  );
  const selectedCompletedUploads = React.useMemo(
    () => filter(selectedUploads, (uploadRow) => uploadRow.uploadCategory !== 'awaitingIngest'),
    [selectedUploads]
  );
  const uploadsWithExistingSlideOutput = React.useMemo(
    () => filter(selectedCompletedUploads, (uploadRow) => Boolean(uploadRow.willCreateDuplicateOnReingest)),
    [selectedCompletedUploads]
  );
  const requiresReingestConfirmation = size(selectedCompletedUploads) > 0;

  React.useEffect(() => {
    if (previousRowsResetKeyRef.current === rowsResetKey) {
      return;
    }

    previousRowsResetKeyRef.current = rowsResetKey;
    reset({
      selectedUploadKeys: defaultSelectedUploadKeys,
      jobName: '',
      jobDescription: '',
      confirmReingest: false,
    });
    setActiveStep(0);
  }, [defaultSelectedUploadKeys, reset, rowsResetKey]);

  React.useEffect(() => {
    setIsStepFailed((currentStepFailures) => ({
      ...currentStepFailures,
      [SELECT_FOLDER_AND_FILES_STEP_INDEX]: Boolean(selectionError),
    }));
  }, [selectionError]);

  React.useEffect(() => {
    setIsStepFailed((currentStepFailures) => ({
      ...currentStepFailures,
      [CONFIRM_REINGEST_STEP_INDEX]: requiresReingestConfirmation ? !confirmReingest : false,
    }));
  }, [confirmReingest, requiresReingestConfirmation]);

  const uploadIngestMutation = useMutation({
    mutationFn: ({
      selectedUploadKeys: requestedUploadKeys,
      jobName: requestedJobName,
      jobDescription,
    }: BulkUploadIngestExecutionFormValues) =>
      executeUploadedSlideIngest({
        studyId,
        uploadKeys: requestedUploadKeys,
        labId,
        reingest: requiresReingestConfirmation,
        jobName: requestedJobName,
        jobDescription,
      }),
    onSuccess: (response) => {
      enqueueSnackbar(response.message, { variant: 'success' });
      void queryClient.invalidateQueries({
        queryKey: ['studyUploads', studyId, labId],
        exact: true,
      });
      onClose();
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.response?.body?.message || 'Failed to queue bulk uploaded slide ingest.', {
        variant: 'error',
      });
    },
  });

  const stepFieldNames: Array<Array<keyof BulkUploadIngestExecutionFormValues>> = React.useMemo(
    () => [['selectedUploadKeys'], [], [], ['confirmReingest']],
    []
  );

  const checkValidationAndSetIsStepFailed = React.useCallback(
    async (stepIndex: number) => {
      const isValid = await trigger(stepFieldNames[stepIndex]);

      setIsStepFailed((currentStepFailures) => ({
        ...currentStepFailures,
        [stepIndex]: !isValid,
      }));

      return isValid;
    },
    [stepFieldNames, trigger]
  );

  const steps = React.useMemo(
    () => [
      {
        label: 'Select folder and files',
        subLabel:
          size(selectedUploadKeys) > 0
            ? `${size(selectedUploadKeys)} file${size(selectedUploadKeys) === 1 ? '' : 's'} selected`
            : undefined,
        content: (
          <Stack spacing={2}>
            <Alert severity="info">
              Choose uploaded files from one folder. Each selected file will be queued for ingest.
            </Alert>
            <Controller
              name="selectedUploadKeys"
              control={control}
              rules={{
                validate: (value) => getUploadSelectionError(value, rows, canReingestUploadedSlides) || true,
              }}
              render={({ field }) => (
                <Stack spacing={1.5}>
                  {canReingestUploadedSlides && !initialSelectedUploadKeys ? (
                    <Alert severity="warning">
                      Completed uploads are not selected by default. Select them only when you explicitly want to queue
                      a re-ingest for those files.
                    </Alert>
                  ) : null}
                  <UploadedFilesTreeSelector
                    rows={rows}
                    selectedUploadKeys={field.value || []}
                    canReingestUploadedSlides={canReingestUploadedSlides}
                    onSelectedUploadKeysChange={(nextUploadKeys) => {
                      field.onChange(nextUploadKeys);
                      setValue('confirmReingest', false, { shouldValidate: true });
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {size(field.value || [])} files selected.
                  </Typography>
                  {errors.selectedUploadKeys?.message || (selectedUploadKeys.length > 0 && selectionError) ? (
                    <FormHelperText error>
                      {String(errors.selectedUploadKeys?.message || selectionError)}
                    </FormHelperText>
                  ) : null}
                </Stack>
              )}
            />
          </Stack>
        ),
      },
      {
        label: 'Job name and description',
        subLabel: activeStep !== JOB_METADATA_STEP_INDEX ? jobName : undefined,
        optional: true,
        content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
      },
      {
        label: 'Review selection',
        subLabel: `${size(selectedUploads)} selected ${size(selectedUploads) === 1 ? 'file' : 'files'}`,
        content: (
          <Stack spacing={2}>
            <Alert severity={requiresReingestConfirmation ? 'warning' : 'info'}>
              {requiresReingestConfirmation
                ? 'The selected files include completed uploads. Submitting this flow will queue those files for re-ingest.'
                : 'Submitting this flow will queue the selected files for ingest.'}
            </Alert>
            {size(uploadsWithExistingSlideOutput) > 0 ? (
              <Alert severity="warning">
                Duplicate slide entries will be created for{' '}
                {map(uploadsWithExistingSlideOutput, (uploadRow) => uploadRow.fileName || uploadRow.name).join(', ')}.
              </Alert>
            ) : null}
            <Stack spacing={0.75}>
              <Typography variant="body2">
                <strong>Selected files:</strong> {size(selectedUploads)}
              </Typography>
              <Typography variant="body2">
                <strong>Parent job:</strong> {jobName || 'Default backend-generated bulk ingest name'}
              </Typography>
              <Typography variant="body2">
                <strong>Description:</strong> {watch('jobDescription') || '—'}
              </Typography>
            </Stack>
            <Stack sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }} spacing={1}>
              <Stack spacing={1}>
                {map(selectedUploads, (uploadRow) => (
                  <Stack key={uploadRow.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {uploadRow.fileName || uploadRow.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={uploadRow.uploadCategory === 'awaitingIngest' ? 'Ingest' : 'Re-ingest'}
                      color={uploadRow.uploadCategory === 'awaitingIngest' ? 'default' : 'warning'}
                    />
                    {uploadRow.willCreateDuplicateOnReingest ? (
                      <Chip size="small" variant="outlined" color="warning" label="Duplicate slide entry" />
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>
        ),
      },
      {
        label: 'Confirm re-ingest',
        subLabel: confirmReingest ? 'Re-ingest confirmed' : 'Confirmation required',
        hidden: !requiresReingestConfirmation,
        content: (
          <Stack spacing={2}>
            <Alert severity="warning">
              Re-ingesting completed uploads should be used only when you explicitly want new ingest jobs for those
              files.
            </Alert>
            <Controller
              name="confirmReingest"
              control={control}
              rules={{
                validate: (value) =>
                  !requiresReingestConfirmation ||
                  value ||
                  'Confirm re-ingest before queuing a bulk ingest that includes completed uploads.',
              }}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(field.value)}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label="I understand the selected completed uploads will be queued for re-ingest."
                />
              )}
            />
            {errors.confirmReingest?.message ? (
              <FormHelperText error>{String(errors.confirmReingest.message)}</FormHelperText>
            ) : null}
          </Stack>
        ),
      },
    ],
    [
      activeStep,
      canReingestUploadedSlides,
      initialSelectedUploadKeys,
      confirmReingest,
      control,
      uploadsWithExistingSlideOutput,
      errors,
      jobName,
      register,
      requiresReingestConfirmation,
      selectedUploadKeys,
      selectionError,
      rows,
      selectedUploads,
      setValue,
      watch,
    ]
  );

  const handleDialogClose = () => {
    if (uploadIngestMutation.isLoading) {
      return;
    }

    onClose();
  };

  const handleExecute = handleSubmit((formValues) => {
    uploadIngestMutation.mutate(formValues);
  });
  const lastVisibleStepIndex = steps.reduce(
    (currentLastStepIndex, step, stepIndex) => (step.hidden ? currentLastStepIndex : stepIndex),
    0
  );
  const isExecuteDisabled = uploadIngestMutation.isLoading || activeStep !== lastVisibleStepIndex || some(isStepFailed);

  return (
    <Dialog open onClose={handleDialogClose} maxWidth="md" fullWidth aria-labelledby="bulk-upload-ingest-dialog-title">
      <DialogChromeHeader
        title={initialSelectedUploadKeys ? 'Ingest selected uploads' : 'Bulk Ingest Uploaded Folder'}
        subtitle={
          initialSelectedUploadKeys
            ? 'Review the selected uploads before queuing ingest.'
            : 'Select uploaded files or folders to queue them for ingest.'
        }
        titleId="bulk-upload-ingest-dialog-title"
        closeButtonAriaLabel="Close bulk upload ingest dialog"
        onClose={handleDialogClose}
        sticky
      />
      <DialogContent dividers>
        <ActionStepper
          activeStateHandlers={[activeStep, setActiveStep]}
          hideExecution
          handleSubmit={handleExecute}
          isStepFailed={isStepFailed}
          setActiveStepForValidation={setActiveStep}
          steps={steps}
          validateStep={() => checkValidationAndSetIsStepFailed(activeStep)}
        />
      </DialogContent>
      <DialogChromeFooter sticky>
        <Button onClick={handleDialogClose} disabled={uploadIngestMutation.isLoading} color="secondary">
          Cancel
        </Button>
        <Button variant="contained" disabled={isExecuteDisabled} onClick={handleExecute} sx={dialogPrimaryActionSx}>
          {uploadIngestMutation.isLoading ? 'Executing...' : 'Execute'}
        </Button>
      </DialogChromeFooter>
    </Dialog>
  );
};

export default BulkUploadIngestExecutionDialog;
